<template>
    <div class="ListForm flex flex-col gap-4">
        <!-- Key used for re-rendering when number of items changes -->
        <VueDraggable
            v-if="value.length"
            :key="value.length"
            v-model="list"
            :animation="200"
            class="flex flex-col"
            :class="inline ? 'gap-5' : 'gap-10'"
            :disabled="!isDraggable || $attrs.disabled || value.length <= 1"
            ghost-class="opacity-25"
            handle=".handle"
            item-key="index"
        >
            <template #item="{ element: item, index }">
                <div
                    class="relative flex w-full"
                    :class="inline ? 'flex-row items-center gap-3' : 'flex-row items-start gap-1.5'"
                >
                    <div
                        v-if="isDraggable && value.length > 1"
                        class="handle flex aspect-square cursor-move items-center justify-center p-2"
                        :class="[
                            { '-ml-2 mt-[30px]': !inline },
                            $attrs.disabled ? 'cursor-not-allowed opacity-50' : ''
                        ]"
                    >
                        <CIcon :class="{ 'rotate-90': !inline }" name="drag-dots-horizontal" />
                    </div>

                    <div class="z-1 flex items-center" :class="inline ? 'order-2' : 'absolute -top-3 right-0'">
                        <CButton
                            v-if="!hasReachedMaxItems && !inline"
                            :disabled="$attrs.disabled"
                            icon="add"
                            variant="primary-link"
                            @click.prevent="addItem(index)"
                        >
                            <span class="hidden md:block">Pridať nad</span>
                        </CButton>

                        <CButton
                            :class="{ 'aspect-square': inline }"
                            :disabled="$attrs.disabled"
                            icon="delete"
                            variant="primary-link"
                            @click.prevent="removeItem(index)"
                        >
                            <span v-if="!inline" class="hidden md:block">Zmazať</span>
                        </CButton>
                    </div>

                    <component
                        v-bind="$attrs"
                        :is="formComponent"
                        class="flex-1"
                        :class="{ 'order-1': inline }"
                        :model-value="item"
                        @update:model-value="updateItem(index, $event)"
                    />
                </div>
            </template>
        </VueDraggable>

        <CButton
            v-if="!hasReachedMaxItems"
            class="self-start"
            :disabled="$attrs.disabled"
            icon="add"
            variant="primary-link"
            @click.prevent="addItem()"
        >
            {{ newItemLabel || 'Pridať' }}
        </CButton>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { PropType, Component } from 'vue'
// @ts-ignore - vuedraggable doesn't have proper types
import VueDraggable from 'vuedraggable/src/vuedraggable.js'

import { addArrayItem, removeArrayItem, updateArrayItem } from '#shared/utils'
import CButton from '@/components/ui/CButton.vue'
import CIcon from '@/components/ui/CIcon.vue'

const props = defineProps({
    value: {
        type: Array,
        default: () => []
    },
    formComponent: {
        type: Object as PropType<Component>,
        required: true
    },
    newItemLabel: {
        type: String,
        default: ''
    },
    newItemGenerator: {
        type: Function,
        required: true
    },
    maxItems: {
        type: Number,
        default: undefined
    },
    inline: {
        type: Boolean as PropType<boolean>,
        default: false
    },
    isDraggable: {
        type: Boolean as PropType<boolean>,
        default: false
    }
})

const emit = defineEmits(['input'])

const hasReachedMaxItems = computed((): boolean => {
    return props.maxItems !== undefined && props.value.length >= props.maxItems
})

const list = computed({
    get() {
        return props.value
    },
    set(items: any[]) {
        emit('input', items)
    }
})

function addItem(index?: number) {
    const newItem = props.newItemGenerator()
    emit('input', addArrayItem(props.value, newItem, index))
}

function removeItem(index: number) {
    emit('input', removeArrayItem(props.value, index))
}

function updateItem(index: number, item: any) {
    console.log('ListEditForm updateItem called:', { index, item })
    const updatedArray = updateArrayItem(props.value, index, item)
    console.log('Updated array:', updatedArray)
    emit('input', updatedArray)
}
</script>